import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DEFAULT_POSITION = { latitude: 36.7538, longitude: 3.0588 };

const validCoordinates = (coordinates) => {
    const latitude = Number(coordinates?.latitude);
    const longitude = Number(coordinates?.longitude);
    return (
        Number.isFinite(latitude)
        && Number.isFinite(longitude)
        && latitude >= -90
        && latitude <= 90
        && longitude >= -180
        && longitude <= 180
    );
};

const normalizeCoordinates = ({ latitude, longitude }) => ({
    latitude: Number(Number(latitude).toFixed(6)),
    longitude: Number(Number(longitude).toFixed(6)),
});

const SalonMap = ({ coordinates, onChange, disabled = false }) => {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        if (!containerRef.current || mapRef.current) return undefined;

        const initial = validCoordinates(coordinates)
            ? normalizeCoordinates(coordinates)
            : DEFAULT_POSITION;
        const map = L.map(containerRef.current, {
            zoomControl: false,
            attributionControl: false,
            zoomAnimation: false,
        }).setView([initial.latitude, initial.longitude], 13);

        L.control.zoom({ position: 'topright' }).addTo(map);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors',
        }).addTo(map);

        const marker = L.marker(
            [initial.latitude, initial.longitude],
            { draggable: !disabled },
        ).addTo(map);

        if (!validCoordinates(coordinates)) {
            marker.setOpacity(0);
        }

        const emitPosition = (latitude, longitude) => {
            const next = normalizeCoordinates({ latitude, longitude });
            marker.setLatLng([next.latitude, next.longitude]);
            marker.setOpacity(1);
            onChangeRef.current?.(next);
        };

        const handleDragEnd = (event) => {
            const position = event.target.getLatLng();
            emitPosition(position.lat, position.lng);
        };
        const handleClick = (event) => {
            emitPosition(event.latlng.lat, event.latlng.lng);
        };

        if (!disabled) {
            marker.on('dragend', handleDragEnd);
            map.on('click', handleClick);
        }

        mapRef.current = map;
        markerRef.current = marker;
        window.setTimeout(() => map.invalidateSize(), 0);

        const handleResize = () => map.invalidateSize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            marker.off('dragend', handleDragEnd);
            map.off('click', handleClick);
            map.remove();
            mapRef.current = null;
            markerRef.current = null;
        };
        // The map is recreated only when its interactivity mode changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled]);

    useEffect(() => {
        if (!mapRef.current || !markerRef.current) return;

        if (!validCoordinates(coordinates)) {
            markerRef.current.setOpacity(0);
            return;
        }

        const next = normalizeCoordinates(coordinates);
        markerRef.current.setLatLng([next.latitude, next.longitude]);
        markerRef.current.setOpacity(1);
        mapRef.current.setView([next.latitude, next.longitude], 15);
    }, [coordinates]);

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
                {t('pro_dashboard.salon.map_location')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('pro_dashboard.salon.click_map_instruction')}
            </Typography>
            <Box
                ref={containerRef}
                sx={{
                    height: 320,
                    width: '100%',
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            />
            {validCoordinates(coordinates) && (
                <Typography variant="caption" color="text.secondary">
                    {t('pro_dashboard.salon.coordinates')}: {' '}
                    {Number(coordinates.latitude).toFixed(6)}, {' '}
                    {Number(coordinates.longitude).toFixed(6)}
                </Typography>
            )}
        </Box>
    );
};

export default SalonMap;
